import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import AuthLink from './AuthLink';

interface LoginFormProps {
  onLoginSuccess: (token: string) => void;
}

const LoginFormC: React.FC<LoginFormProps> = ({ onLoginSuccess }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await axios.post('/api/login', { email, password });
      onLoginSuccess(response.data.token);
    } catch (err) {
      setError(t('errors.invalid_credentials'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto bg-gray-800/50 backdrop-blur-lg border border-gray-700 rounded-lg p-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">
          {t('auth.welcome_message')}
        </h1>
        <p className="text-gray-400">{t('auth.login_subtitle')}</p>
      </div>
      
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 text-red-300 px-4 py-3 rounded-md mb-6">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {t('auth.email_label')}
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
            placeholder={t('auth.email_placeholder')}
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {t('auth.password_label')}
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
            placeholder={t('auth.password_placeholder')}
            required
          />
        </div>
        
        <button
          type="submit"
          disabled={isLoading || !email.trim() || !password.trim()}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-600 disabled:to-gray-600 text-white font-semibold py-3 px-4 rounded-md transition-all duration-300 disabled:cursor-not-allowed"
        >
          {isLoading ? t('auth.logging_in') : t('auth.login_button')}
        </button>
      </form>
      
      <div className="mt-8 text-center space-y-4">
        <AuthLink 
          to="/register" 
          className="block text-purple-400 hover:text-purple-300 transition-colors"
        >
          {t('auth.no_account_register')}
        </AuthLink>
        <Link 
          to="/forgot-password" 
          className="block text-gray-400 hover:text-gray-300 transition-colors text-sm"
        >
          {t('auth.forgot_password')}
        </Link>
        <Link
          to="/verify-email"
          className="block text-gray-400 hover:text-gray-300 transition-colors text-sm"
        >
          {t('email_verification.resend_button')}
        </Link>
      </div>
    </div>
  );
};

export default LoginFormC; 